import React, { useEffect, useRef, useState } from 'react';
import { uses12h, weekStartsOn } from '../utils/timeFormat';
import { format, addDays, subDays, startOfWeek, parseISO, isToday as fnsIsToday } from 'date-fns';
import apiClient from '../api/client';
import { weekEventsCache } from '../utils/viewCache';
import EventCard from './EventCard';
import LabelsMenu from './LabelsMenu';
import { SpeakerIcon, StopIcon } from './icons';
import { useAuth } from '../context/AuthContext';
import { useT, DATE_LOCALES } from '../i18n';
import { applyTTSPrefs } from '../utils/ttsUtils';
import { claimSwipe, releaseSwipe } from '../utils/swipeOwner';

function langToSpeechLang(lang) {
  const map = {
    it: 'it-IT', en: 'en-US', 'en-US': 'en-US', 'en-GB': 'en-GB',
    es: 'es-ES', 'es-ES': 'es-ES', 'es-AR': 'es-AR', 'es-MX': 'es-MX', 'es-CO': 'es-CO',
    de: 'de-DE', fr: 'fr-FR', nl: 'nl-NL',
    pt: 'pt-PT', 'pt-PT': 'pt-PT', 'pt-BR': 'pt-BR',
    ru: 'ru-RU', pl: 'pl-PL', ro: 'ro-RO', zh: 'zh-CN', ja: 'ja-JP', hi: 'hi-IN', ko: 'ko-KR', ar: 'ar-SA',
  };
  return map[lang] || 'it-IT';
}

function timePreposition(lang) {
  const map = {
    it: 'alle', en: 'at', es: 'a las', de: 'um', fr: 'à', nl: 'om', pt: 'às',
    ru: 'в', pl: 'o', ro: 'la', zh: '', ja: '', hi: 'को', ko: '', ar: 'الساعة',
  };
  const v = map[lang] ?? map[lang?.split('-')[0]];
  return v !== undefined ? v : 'at';
}

function priorityLabel(priority, lang) {
  const base = lang?.split('-')[0];
  const labels = {
    it: { red: 'priorità alta', yellow: 'priorità media', green: 'priorità bassa' },
    en: { red: 'high priority', yellow: 'medium priority', green: 'low priority' },
    es: { red: 'prioridad alta', yellow: 'prioridad media', green: 'prioridad baja' },
    de: { red: 'hohe Priorität', yellow: 'mittlere Priorität', green: 'niedrige Priorität' },
    fr: { red: 'haute priorité', yellow: 'priorité moyenne', green: 'basse priorité' },
    nl: { red: 'hoge prioriteit', yellow: 'gemiddelde prioriteit', green: 'lage prioriteit' },
    pt: { red: 'alta prioridade', yellow: 'média prioridade', green: 'baixa prioridade' },
    ru: { red: 'высокий приоритет', yellow: 'средний приоритет', green: 'низкий приоритет' },
    pl: { red: 'wysoki priorytet', yellow: 'średni priorytet', green: 'niski priorytet' },
    ro: { red: 'prioritate înaltă', yellow: 'prioritate medie', green: 'prioritate scăzută' },
    zh: { red: '高优先级', yellow: '中优先级', green: '低优先级' },
    ja: { red: '優先度：高', yellow: '優先度：中', green: '優先度：低' },
    hi: { red: 'उच्च प्राथमिकता', yellow: 'मध्यम प्राथमिकता', green: 'कम प्राथमिकता' },
    ko: { red: '높은 우선순위', yellow: '중간 우선순위', green: '낮은 우선순위' },
    ar: { red: 'أولوية عالية', yellow: 'أولوية متوسطة', green: 'أولوية منخفضة' },
  };
  return labels[lang]?.[priority] || labels[base]?.[priority] || '';
}

// weather is passed down from TodayView (single useWeather instance for the whole day view)
export default function WeekView({ weather, activeSubCal, planBtn }) {
  const { user } = useAuth();
  const T = useT(user?.language);
  const locale = DATE_LOCALES[user?.language] || DATE_LOCALES['it'];
  const [isSpeaking, setIsSpeaking] = useState(false);
  // The audio coming back from the server, and a token so that pressing Stop
  // while it is still on its way actually stops it — see the day view, where
  // the same request once started talking a second after the button said it
  // had stopped.
  const audioRef = useRef(null);
  const speakTokenRef = useRef(0);

  const [weekStart, setWeekStart] = useState(() =>
    format(startOfWeek(new Date(), { weekStartsOn: weekStartsOn() }), 'yyyy-MM-dd')
  );
  // The week we are actually in, so the way home knows where home is.
  const thisWeekStart = format(startOfWeek(new Date(), { weekStartsOn: weekStartsOn() }), 'yyyy-MM-dd');
  const [weekEvents, setWeekEvents] = useState({});
  const [loading, setLoading] = useState(true);

  // Capital first letter on the weekday, as in Oggi: these are headings, and
  // several languages write the day lower case in running text.
  const cap = (w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w);
  const weekDays = Array.from({ length: 7 }, (_, i) =>
    format(addDays(parseISO(weekStart), i), 'yyyy-MM-dd')
  );

  const weekStartObj = parseISO(weekStart);
  const weekEndObj = addDays(weekStartObj, 6);

  function prevWeek() { setWeekStart(d => format(subDays(parseISO(d), 7), 'yyyy-MM-dd')); }
  function nextWeek() { setWeekStart(d => format(addDays(parseISO(d), 7), 'yyyy-MM-dd')); }

  // A sideways drag moves a week here exactly as it moves a day on Today —
  // same thresholds, same reasons, same refusal to touch the vertical scroll.
  // See utils/swipeOwner.js for why the two cannot both own the gesture.
  useEffect(() => {
    claimSwipe('week');
    let x0 = 0, y0 = 0, t0 = 0, live = false;
    function start(e) {
      const t = e.touches && e.touches[0];
      if (!t) { live = false; return; }
      if (e.target?.closest?.('button, a, input, textarea, select, [role="button"]')) { live = false; return; }
      x0 = t.clientX; y0 = t.clientY; t0 = e.timeStamp; live = true;
    }
    function end(e) {
      if (!live) return;
      live = false;
      const t = e.changedTouches && e.changedTouches[0];
      if (!t) return;
      const dx = t.clientX - x0, dy = t.clientY - y0;
      if (e.timeStamp - t0 > 800) return;
      if (Math.abs(dx) < 60) return;
      if (Math.abs(dx) < Math.abs(dy) * 1.8) return;
      const rtl = document.documentElement.getAttribute('dir') === 'rtl';
      const back = rtl ? dx < 0 : dx > 0;
      if (back) prevWeek(); else nextWeek();
    }
    document.addEventListener('touchstart', start, { passive: true });
    document.addEventListener('touchend', end, { passive: true });
    return () => {
      releaseSwipe('week');
      document.removeEventListener('touchstart', start);
      document.removeEventListener('touchend', end);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    // A week you've already seen appears at once, and is refreshed behind your
    // back — no spinner, no empty screen. Same trick Today already used.
    const cached = weekEventsCache.get(weekStart);
    if (cached) {
      setWeekEvents(cached);
      setLoading(false);
    } else {
      setLoading(true);
      setWeekEvents({});
    }
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // One request for the whole week (the old 7 parallel calls multiplied
    // the pain of a cold-starting server). `cancelled` guards against a slow
    // earlier request landing after a faster later one (rapid week paging).
    apiClient.get('/events/week', { params: { tz, start: weekStart } })
      .then(res => {
        if (cancelled) return;
        weekEventsCache.set(weekStart, res.data || {});
        setWeekEvents(res.data || {});
      })
      .catch(() => { if (!cancelled && !cached) setWeekEvents({}); })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        // The week before and the week after, fetched quietly once this one is
        // on screen. Silvia: "la settimana scorre ma ci mette qualche secondo a
        // caricare". It was not slow code — it was a round trip to the server
        // and on to Google, every single time, because a week you have not
        // opened yet cannot be in the cache.
        //
        // Now the two you are most likely to ask for next are already there,
        // so a drag lands on a week that is drawn instantly. Two extra requests
        // per week actually visited, made after the screen is filled, and never
        // repeated: a week already in the cache is skipped.
        const tz2 = Intl.DateTimeFormat().resolvedOptions().timeZone;
        [-7, 7].forEach(delta => {
          const w = format(addDays(parseISO(weekStart), delta), 'yyyy-MM-dd');
          if (weekEventsCache.has(w)) return;
          apiClient.get('/events/week', { params: { tz: tz2, start: w } })
            .then(r => weekEventsCache.set(w, r.data || {}))
            .catch(() => { /* it will simply be fetched when it is opened */ });
        });
      });
    return () => { cancelled = true; };
  }, [weekStart]);

  // "24-30 agosto 26" inside one month, "24 ago - 3 set 26" across two.
  // It used to read "Settimana 24 ago – 30 ago 2026": the month written twice,
  // the year in full, and the word "Settimana" above a screen that is visibly
  // a week. Silvia asked for the short forms and she is right — the heading is
  // there to say WHICH week, and everything else was already on the screen.
  const sameMonth = format(weekStartObj, 'yyyy-MM') === format(weekEndObj, 'yyyy-MM');
  // Non-breaking spaces: HTML would collapse ordinary ones back to a single
  // space and the gap Silvia asked for would not appear at all.
  const GAP = '\u00A0\u00A0';
  const days = sameMonth
    ? `${format(weekStartObj, 'd', { locale })}-${format(weekEndObj, 'd', { locale })}` + GAP + format(weekEndObj, 'MMMM', { locale })
    : `${format(weekStartObj, 'd', { locale })}${GAP}${format(weekStartObj, 'MMM', { locale })} - ${format(weekEndObj, 'd', { locale })}${GAP}${format(weekEndObj, 'MMM', { locale })}`;
  // Just the number in brackets. Silvia: "essendo che i numeri vanno in
  // progressione, a chi serve questa funzione la capisce" — and it saves
  // fifteen abbreviations that each had to be right in their own language.
  const weekNo = format(weekStartObj, 'I', { locale });
  const weekLabel = days + GAP + format(weekEndObj, 'yy', { locale }) + GAP + '(' + weekNo + ')';

  // Cancel speech on unmount or when navigating away
  useEffect(() => {
    return () => { window.speechSynthesis?.cancel(); };
  }, []);

  // Stop speech when week changes
  useEffect(() => {
    window.speechSynthesis?.cancel();
    setIsSpeaking(false);
  }, [weekStart]);

  // Respect the sub-calendar filter chips shared with the Day view
  const evtsOf = (d) => (weekEvents[d] || []).filter(ev => !activeSubCal || ev.subCalendar?.id === activeSubCal);

  const totalEvents = weekDays.reduce((sum, d) => sum + evtsOf(d).length, 0);

  useEffect(() => () => {
    try { window.speechSynthesis?.cancel(); } catch (_) {}
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
  }, []);

  function handleSpeak() {
    if (isSpeaking) {
      try { window.speechSynthesis?.cancel(); } catch (_) {}
      speakTokenRef.current++;              // anything still coming is now stale
      if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
      setIsSpeaking(false);
      return;
    }

    const lang = user?.language || 'it';
    // This device's clock, same as the cards — see the note in TodayView:
    // using the account's saved timezone made the voice read a different hour
    // from the one shown on screen.
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const speechLang = langToSpeechLang(lang);
    const prep = timePreposition(lang);
    const use12h = uses12h(lang);

    const dayTexts = weekDays
      .map(dayStr => {
        const dayEvts = evtsOf(dayStr);
        if (dayEvts.length === 0) return null;
        const dayName = format(parseISO(dayStr), 'EEEE d MMMM', { locale });
        const eventsText = dayEvts.map(ev => {
          const time = ev.start
            ? new Date(ev.start).toLocaleTimeString(lang || 'it', {
                hour: '2-digit', minute: '2-digit', hour12: use12h, timeZone: timezone,
              })
            : '';
          const pLabel = priorityLabel(ev.priority, lang);
          return `${time ? prep + ' ' + time + ', ' : ''}${ev.title}${pLabel ? '. ' + pLabel : ''}.`;
        }).join(' ');
        return `${dayName}: ${eventsText}`;
      })
      .filter(Boolean);

    const fullText = `${T.today.weekOf} ${weekLabel}. ` + dayTexts.join('. ');
    setIsSpeaking(true);

    if (window.speechSynthesis) {
      const utterance = new SpeechSynthesisUtterance(fullText);
      utterance.lang = speechLang;
      applyTTSPrefs(utterance); // voice + pitch + user-chosen speed
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = () => setIsSpeaking(false);
      window.speechSynthesis.speak(utterance);
      return;
    }

    // No engine here — ask the server for the sound, exactly as the day view
    // does. This is the whole reason the speaker had vanished from the week:
    // inside the installed Android app there is no speechSynthesis at all, so
    // the button was hidden and the week could not be read out even though the
    // day could.
    speakServer(fullText);
  }

  async function speakServer(text) {
    const token = ++speakTokenRef.current;
    try {
      const res = await apiClient.post('/speak',
        { text, language: user?.language || 'it' },
        { responseType: 'blob' });
      if (token !== speakTokenRef.current) return;   // stopped, or asked again
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => { URL.revokeObjectURL(url); audioRef.current = null; setIsSpeaking(false); };
      audio.onerror = () => setIsSpeaking(false);
      await audio.play();
    } catch (_) {
      // Say nothing false: the button simply goes back to being a speaker.
      setIsSpeaking(false);
    }
  }

  return (
    <div className="week-view">
      {/* Same shape as the day: the date on its own line, and under it the
          planner and the speaker together on the right. No arrows — a sideways
          drag changes the week, exactly as it changes the day. Silvia: "fammi
          scorrere anche la settimana così l'app funziona uguale in tutte le
          sezioni". The two hidden buttons stay for TalkBack, which takes the
          swipe for itself and would otherwise have no way through. */}
      <div className="date-nav" style={{ marginBottom: 6, position: 'relative' }}>
        <button className="visually-hidden" onClick={prevWeek}>{T.today.prevWeek}</button>
        {/* The way back, for the same reason as the day: a drag moves one week
            at a time and there is nothing else to press. */}
        {weekStart !== thisWeekStart && (
          <button
            type="button"
            onClick={() => setWeekStart(thisWeekStart)}
            aria-label={T.nav.today}
            title={T.nav.today}
            style={{
              position: 'absolute', insetInlineStart: 0, top: '50%', transform: 'translateY(-50%)',
              width: 32, height: 32, borderRadius: '50%', border: 'none',
              background: 'var(--color-primary)', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', padding: 0, zIndex: 2,
            }}
          >
            <span className="rtl-flip" style={{ display: 'flex' }}>
              <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true" stroke="currentColor" fill="none" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
            </span>
          </button>
        )}
        <div className="date-nav-center" style={{ paddingInlineStart: weekStart !== thisWeekStart ? 32 : 0, paddingInlineEnd: weekStart !== thisWeekStart ? 32 : 0, minWidth: 0 }}>
          <div className="today-date">{weekLabel}</div>
        </div>
        <button className="visually-hidden" onClick={nextWeek}>{T.today.nextWeek}</button>
      </div>

      {/* Planner and speaker on their own line, aligned left — the same
          arrangement as the day, for the same reason: four things do not fit
          across a phone without cutting the one that matters. */}
      {/* The row is no longer conditional on there being events: the labels
          menu is most useful precisely on an empty week, and it hides itself
          when there is genuinely nothing to list. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, padding: '2px 0 6px' }}>
          <LabelsMenu />
          {planBtn}
          {!loading && totalEvents > 0 && (
            <button
            className={`tts-btn${isSpeaking ? ' tts-btn--stop' : ''}`}
            onClick={handleSpeak}
            title={isSpeaking ? T.today.ttsStop : T.today.ttsButton}
            aria-label={isSpeaking ? T.today.ttsStop : T.today.ttsButton}
            >
            {isSpeaking ? <StopIcon /> : <SpeakerIcon />}
            </button>
          )}
      </div>

      {loading && <div className="spinner" />}

      {/* Continuous day list */}
      {!loading && weekDays.map(dayStr => {
        const dayObj = parseISO(dayStr);
        const isToday = fnsIsToday(dayObj);
        const dayEvts = evtsOf(dayStr);

        return (
          <div key={dayStr} className="week-day-section">
            {/* Day header */}
            <div className={`week-day-header${isToday ? ' week-day-header--today' : ''}`}>
              <span className="week-day-header-name">
                {cap(format(dayObj, 'EEE', { locale }))}
              </span>
              <span className={`week-day-header-num${isToday ? ' week-day-header-num--today' : ''}`}>
                {format(dayObj, 'd')}
              </span>
              {!isToday && (
                <span className="week-day-header-month">
                  {format(dayObj, 'MMM', { locale })}
                </span>
              )}
              {isToday && (
                <span className="week-day-header-today-label">
                  {T.nav.today}
                </span>
              )}
              {/* Weather for this day — pushed to the right */}
              {weather?.forecast?.[dayStr] && (
                <span style={{
                  marginInlineStart: 'auto',
                  display: 'flex', alignItems: 'center', gap: 3,
                  fontSize: '0.78rem', fontWeight: 600,
                  color: 'var(--color-text-muted)',
                }}>
                  <span style={{ fontSize: '1rem' }}>{weather.forecast[dayStr].emoji}</span>
                  {weather.forecast[dayStr].temp != null && (
                    <span>{weather.forecast[dayStr].temp}°</span>
                  )}
                </span>
              )}
            </div>

            {/* Events or empty */}
            <div className="week-day-events">
              {dayEvts.length === 0 ? (
                <div className="week-day-empty">{T.today.noEventsDay}</div>
              ) : (
                dayEvts.map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onDelete={(id) => {
                      setWeekEvents(prev => ({
                        ...prev,
                        [dayStr]: prev[dayStr].filter(e => e.id !== id),
                      }));
                    }}
                  />
                ))
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
